/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import { ParameterName } from '../calculator/calculatorTypes';

export type Quantity =
  | 'quantity.temperature'
  | 'quantity.pressure'
  | 'quantity.elevation'
  | 'quantity.relativeHumidity'
  | 'quantity.enthalpy'
  | 'quantity.absoluteHumidity'
  | 'quantity.mixingRatio'
  | 'quantity.gasDensity'
  | 'quantity.specificVolume'
  | 'quantity.massFractionDry'
  | 'quantity.volumeFractionDry'
  | 'quantity.volumeFraction';

const unitOptions: UnitOptions[] = [
  { quantity: 'quantity.temperature', options: ['°C', '°F', 'K'] },
  {
    quantity: 'quantity.pressure',
    options: ['hPa', 'Pa', 'mbar', 'kPa', 'mPa', 'bara', 'barg', 'atm', 'mmH2O', 'inH2O', 'psia', 'psig', 'mmHg', 'torr', 'inHg'],
  },
  { quantity: 'quantity.elevation', options: ['m', 'ft'] },
  { quantity: 'quantity.relativeHumidity', options: ['%rh'] },
  { quantity: 'quantity.enthalpy', options: ['kJ/kg', 'BTU/lb'] },
  { quantity: 'quantity.absoluteHumidity', options: ['g/m3', 'gr/ft3'] },
  { quantity: 'quantity.mixingRatio', options: ['g/kg', 'gr/lb', 'ppmw'] },
  { quantity: 'quantity.gasDensity', options: ['kg/m3', 'lb/ft3'] },
  { quantity: 'quantity.specificVolume', options: ['m3/kg', 'ft3/lb'] },

  { quantity: 'quantity.massFractionDry', options: ['ppmwdry'] },
  { quantity: 'quantity.volumeFractionDry', options: ['ppmvoldry'] },
  { quantity: 'quantity.volumeFraction', options: ['ppmvol', 'umol/mol', 'vol-%'] },
];

const extractOptions: any = (q: string) => unitOptions.find((o: UnitOptions) => o.quantity === q)!.options;

const temperatureOps = extractOptions('quantity.temperature');
type TemperatureUnit = (typeof temperatureOps)[number];

const pressureOps = extractOptions('quantity.pressure');
type PressureUnit = (typeof pressureOps)[number];

const elevationOps = extractOptions('quantity.elevation');
type ElevationUnit = (typeof elevationOps)[number];

export type Unit = TemperatureUnit | PressureUnit | ElevationUnit;

export type QuantityMapping = {
  parameter: ParameterName;
  quantity: Quantity;
};

type UnitSelection = {
  parameter: ParameterName;
  unitIndex: number;
};

export type UnitPreset = {
  mode: 'metric' | 'nonMetric' | 'manual';
  selections: UnitSelection[];
};

export type UnitOptions = {
  quantity: Quantity;
  options: Unit[];
};

export const optionsForQuantity = (q: Quantity) => unitOptions.find((o) => o.quantity === q);
